---
title: Getting Started
sidebar_position: 2
description: Getting Started
---

# Getting Started

## Login

### First Login with Planet Account

If you have a Planet account, follow these steps to access the AM App:

#### Step 1: Navigate to the Application

1. Open your web browser
2. Go to [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. You will see the login screen

![AM App login screen with Planet Account button highlighted](../imgs/login-planet.png)

#### Step 2: Select Planet Account Login

1. Click the **"Planet Account"** button on the login screen
2. You will be redirected to Planet's authentication page

#### Step 3: Link Accounts (First-Time Users Only)

If this is your first time logging in with a Planet account:

1. After authenticating with Planet, you'll see an account linking screen
2. Select **"Add to existing account"** option
3. A verification email will be sent to your email address from `noreply@planet.com`
4. **Important**: Check your email and click the confirmation link within **5 minutes**
5. If the link expires, you'll need to restart the login process

#### Step 4: Accept Terms of Use

1. Read the Planet Labs Terms of Use
2. Check the acceptance box
3. Click `Accept` to proceed

#### Step 5: Access Granted

Once authenticated, you'll be directed to the Overview and can begin using the application.

---

### First Login with Dedicated AMS Credentials

If you have been provided with dedicated Area Monitoring Service credentials:

#### Step 1: Navigate to the Application

1. Open your web browser
2. Go to [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. You will see the login screen

![Login screen with username/password fields highlighted](../imgs/login-ams.png)

#### Step 2: Enter Credentials

1. Enter your **username** or **email**
2. Enter your **password**
3. Click `Sign In`

#### Step 3: Accept Terms of Use

1. On your first login, read the Planet Labs Terms of Use
2. Check the acceptance box
3. Click `Accept` to proceed

#### Step 4: Access Granted

Once authenticated, you'll be directed to the Overview and can begin using the application.

## Selecting a Scope

After logging in, your first task is to select a scope:

1. Look for the **scope dropdown** on the left side of the application header
2. Click the dropdown to see your available scopes _(there's no drop-down option if only one scope is available)_

![Scope selection dropdown with `DEMO_FR23` highlighted](../imgs/scope-selection.png)

### Selecting Your Scope

- `DEMO_FR23`: Demonstration scope is available to all Planet account users
- **Project-specific scopes** to whose access has to be granted by scope user administrator

1. Click on the scope name to select it
2. The application will load the FOIs and data for that scope
3. All subsequent data and filters will be specific to this scope

**Important**: The scope you select determines:

- Which geographic area you're monitoring
- What time periods have available data
- Which signals and markers are calculated
- All filter options throughout the application

You can change scopes at any time by using the scope dropdown again.

## Application Layout Overview

The **Overview Module** is your primary entry point after selecting a scope. It provides:

- A high-level view of all Features of Interest (FOIs) in your scope
- A spatial map showing FOI locations
- Quick access to detailed FOI analysis (FOI View)
- Multiple methods to search and filter FOIs
- A resizable layout — drag the gutter between the table and the map to adjust the split

Use the Overview Module to:

- Browse and explore FOIs in your monitoring area
- Identify FOIs of interest
- Navigate to detailed analysis in the FOI View Module

![Overview](../imgs/overview.png)

### Main Components

**Header Bar** (Top):

- **Scope Dropdown**: Switch between available scopes; when working in the Validation Console, the active Bundle ID and Package ID are also shown here
- **Theme toggle**: dark vs. light
- **Apps Menu**: Switch to other Planet applications
- **User Menu**: Access profile, language settings, and logout

**Collapsible module menu** (Left) — the main workspace changes based on which module you select:

- **Data View**:
  - **Overview** (default): FOI list and map
  - **FOI View**: Detailed FOI analysis with multiple panels
- **Validation Console** (visible to users with Validation Console access):
  - **Bundle Creation**: Define input types and filters to create a bundle of packages _(role: GTC-ADMIN only)_
  - **Bundles**: List of created bundles _(roles: GTC-ADMIN, GTC-OPERATOR or GTC-VIEWER)_
  - **Packages**: List of created packages _(roles: GTC-ADMIN, GTC-OPERATOR or GTC-VIEWER)_
  - **Validation View**: Package-level FOI analysis with multiple panels and user input _(roles: GTC-ADMIN, GTC-OPERATOR or GTC-VIEWER)_
- **Configuration**:
  - **Settings**: Set time range and marker context
  - **Config Editor**: Edit scope configuration _(role: SUPER-SYSADMIN only)_

The central area shows the components specific to the selected module.

![Main Components](../imgs/main-components.png)

## Language Selection

The app is available in English, Czech, and Slovenian.

To switch language:

1. Click the **User Menu** icon in the top-right corner of the header
2. Hover over the **Language** sub-menu item
3. Select your preferred language

The interface updates immediately. Your language choice is saved across sessions.

## Setting Time Range and Marker Context

Before analyzing data, configure your temporal and marker settings in the **Settings** drawer.

### Opening the Settings Drawer

1. Click **Settings** in the collapsible module menu (bottom of the left panel, under **Configuration**)

![Settings Drawer](../imgs/settings.png)

### Setting the Time Range

1. In the Settings drawer, find the **Time Range** section
2. Set your **Start Date**: click the calendar and select the beginning date
3. Set your **End Date**: click the calendar and select the ending date
4. Optionally set a **Selected date**: click the calendar and select the date
   - The map base layer updates to show imagery from that date
   - Useful for seeing spatial context at a specific point in time

**Important**: Data will only populate if valid observations exist within your selected time period. If you see no data, try adjusting your time range to match when data was collected. When first opening a scope, the time range is set to default start and end dates defined in the scope configuration.

### Selecting Marker Context

1. In the Settings drawer, find the **Marker Context** dropdown
2. Click the dropdown to see available marker contexts
3. Select the desired context

Marker contexts let you view different sets of marker calculations, typically representing different calculation runs or seasonal periods.

**Note**: Settings now only control **time range** and **marker context**. Observation display options (columns, layer, padding, valid-filter) and signal/marker visualization options are accessible directly within each component — see [FOI View Module](03-foi-view-module.mdx).

## Map Widget and Navigation

The **Map Widget** displays FOIs with spatial context and interactive tools.

### Map Overview Interface

The map in the Overview Module shows the spatial distribution of your FOIs.

#### Map Features

- **FOI Polygons**: Field boundaries displayed on the map
- **Color coding**: FOIs may be colored by single colour, FOI attributes, or marker results
- **Selected FOI**: highlighted with a yellow outline by default
- **Background layer**: base layers defined in scope configuration
- **H3 hex-grid overlay**: when zoomed out beyond the FOI zoom threshold, FOIs are grouped into hexagonal cells. Click a hex cell to zoom in and reveal the FOIs it contains.

#### Map Controls

- **Pan**: Click and drag to move around the map
- **Zoom**: Use the zoom buttons or mouse wheel to zoom in/out
- **Pan to all**: Button to zoom to show all FOIs in the scope

### Map Search (Go To)

The **Map Search** bar on the map lets you navigate directly to a specific FOI or coordinate location. It supports autocompletion and shows a "No results" message when nothing matches.

#### Navigate by FOI ID

1. Click the search bar on the map
2. Type the **FOI ID** (format: `SCOPE.FOI.123`) or start typing a **FOI Reference ID**
3. Select the matching result from the dropdown
4. The map centers on that FOI and it becomes selected

#### Navigate by Coordinates

1. Click the search bar on the map
2. Enter a coordinate pair in one of the three supported formats:
   - **Decimal degrees** (EPSG:4326): `45.5, 10.2`
   - **Cardinal degrees** (EPSG:4326): `45.5°N, 10.2°E`
   - **Projected metric** (scope CRS): `500000, 5500000`
3. Select the coordinate result from the dropdown
4. The map pans to that location

### Preview Details Panel

Left-clicking an FOI on the map opens a floating **Selection Preview** panel showing a summary of the FOI's attributes — including its key identifiers. This works consistently across the Overview, FOI View, and Validation View, and is the quickest way to identify a parcel and read its details without opening it fully.

- If multiple FOIs overlap at the click point, the panel lists all of them; click one to expand its attributes
- The exact attributes shown depend on the scope's data mapping (see [Scope Configuration](05-scope-configuration.mdx)), so different scopes may expose different fields
- Click
  - **Select FOI** to select previewed FOI
  - **Open in new FOI View tab** to navigate into detailed analysis in new browser tab
  - **Copy** to copy FOI ID

![Selection Preview panel open after clicking a parcel on the map](../imgs/map-selection-preview.png)

### Tools

**Measurement Tool**:

1. Click the **measurement tool** icon
2. Select **Distance** or **Area** mode
3. **For Distance**:
   - Click points on the map to create a line
   - Distance is calculated and displayed (meters or km)
   - Double-click to finish
4. **For Area**:
   - Click points to draw a polygon
   - Area is calculated and displayed — the unit shown depends on scope configuration (m², km², **ha**, or acres)
   - Close the polygon to finish

![Measurement Tool](../imgs/map-measure-tool.png)

**Drop Pin Tool**:

1. Click the **drop pin** icon
2. Click anywhere on the map to place a pin
3. The pin's **precise coordinates** are displayed
4. Use this to copy coordinates
5. Click the pin `X` to remove it

![Drop pin tool](../imgs/map-drop-pin.png)

**Create Photo Task**:

If your account has photo task creation permissions, a **Create photo task** button is available on the map. Select an FOI first, then click the button to open the photo task manager for that field. This button is hidden when photo access is not enabled for the scope.

**Pan and Zoom**:

- **Pan**: Click and drag the map to move
- **Zoom**: Use the zoom buttons or mouse scroll wheel
- **Pan to FOI**: Button to center and zoom to the selected FOI

### Layers Management

#### Base Layer

**Change Background Base Layer**:

1. Click the **Layers** button or icon
2. The layer management panel opens
3. Select from available base layers:
   - **Aerial Photography**: High-resolution aerial imagery
   - **Sentinel or Planet Imagery**: layers defined in linked Planet Insight Platform configuration
   - Other scope-specific layers defined in scope config
4. The map background updates immediately

![Base Layer Management](../imgs/map-base-layers.png)

**Note — layer names and defaults come from scope configuration**: The list of available base layers, their names, and which layer loads by default are all defined by your scope administrator. If a layer name looks wrong or outdated, or a layer fails to load entirely, this is a scope-configuration matter — not something you can fix in the application. Contact your administrator (see [Scope Configuration](05-scope-configuration.mdx)) or raise it with Planet support (see [Support and Troubleshooting](07-support-troubleshooting.mdx)).

#### FOI Visualization Options

**Toggle Labels and Borders for FOIs**:

1. Open the **Layer Settings** panel
2. Find **FOI Visualization** options:
   - **Show Labels**: Display FOI attribute labels (e.g., FOI ID, crop type). Labels appear once you zoom past the per-FOI-type zoom threshold configured for the scope.
   - **Show Borders**: Display FOI polygon outlines
   - **Show Fill**: Fill FOI polygons with color
   - **Opacity**: Adjust transparency (0–100%)
3. Toggle options on/off to customize the display

**Color FOIs By**:
You can color FOIs using different schemes:

1. **Single Color**:
   - Select one of the available colours from the drop-down list
   - All FOIs are visualized with the selected colour

2. **Attributes**:
   - Select one of the available attributes from the drop-down list
   - Colors represent different values of the selected attribute (e.g. crop type)

3. **Marker Results**:
   - FOIs colored based on marker predictions
   - Select one of the available markers from the drop-down list
   - Examples: Green for "approved", Red for "flagged"
   - Colors defined by scope configuration

![FOI Visualization Options](../imgs/map-foi-visualization-options.png)

**Note — label defaults come from scope configuration**: Which labels are shown and whether they are on by default is set per FOI type in the scope configuration. If you open the app and labels are already visible, that is the expected default for your scope — your administrator has configured them that way. User can always override these default display settings to her own liking and needs.

**Selected FOI Highlight**:

- The currently selected FOI is highlighted with a **yellow outline and fill** by default
- Makes it easy to identify which FOI you're analyzing
- Outline color can be changed in layer options

## FOI List/Table Widget

### Features

**Filterable Table**:

- All FOIs in the current scope displayed in a table
- Use the **filter box** at the top to filter FOIs
- Select any attribute and type value to quickly find FOIs

**Column Management**:

- Click **Manage columns** to show/hide columns
- Customize which FOI attributes are displayed
- Columns may include: FOI ID, Type, Crop, Land Use, Area, etc.

**Pagination**:

- Navigate through pages if you have many FOIs
- Page numbers shown at bottom of table

**Quick FOI Switching**:

- Click any FOI row to switch to that FOI
- The currently selected FOI is highlighted in the table

![FOI filter](../imgs/foi-filter-columns.png)

## Finding and Selecting Features of Interest

The AM App provides multiple ways to select Features of Interest. Choose the method that works best for your workflow:

### Method 1: Select in FOI List

1. Look at the **FOI List** panel (on the left side)
2. Click on an FOI in the list
3. The map centers on that FOI and its data loads
4. Click the action button to enter the detailed FOI View

### Method 2: Filter

The filter bar lets you narrow the FOI list by one or more attribute values.

For example, if you know the FOI type and reference ID:

1. Look at the **FOI List** panel (on the left side)
2. **Use the filter bar** at the top of the list
3. Use the attribute selection drop-down to choose the attribute to filter by
4. Use **FOI Type** to select the type (e.g., "Field", "Parcel")
5. Use **FOI Ref ID** to enter one or multiple reference IDs in the filter field
6. The list updates to show only matching FOIs
7. Click the action button to enter the detailed FOI View

![FOI filter](../imgs/foi-filter-foi-type-ref-id.png)

### Method 3: Map Search

Use the **Map Search** bar on the map to jump directly to an FOI by its ID or reference ID, or navigate to a set of coordinates. See [Map Widget and Navigation](#map-widget-and-navigation) for details.

### Method 4: Map Interaction

Select FOIs directly from the map:

1. Find your FOI on the map display
2. **Left-click** an FOI polygon to open the **Selection Preview** panel with its attributes
3. **Right-click** an FOI polygon to go directly into the detailed FOI View

**Tip**: Pan and zoom the map to explore different areas. When zoomed out, click a hex cell to zoom in to that group of FOIs.

For advanced FOI analysis features, see [FOI View Module](03-foi-view-module.mdx).

---
